import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "@/components/Reveal";
import { LeadForm } from "@/components/LeadForm";
import { ShieldCheck, TrendingUp } from "lucide-react";

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden pt-32 pb-20 lg:pt-40" data-testid="hero">
      <motion.div style={{ y: yGlow }} className="ambient-orange pointer-events-none absolute -left-32 -top-20 h-[600px] w-[600px]" />
      <motion.div style={{ y: yGlow }} className="ambient-indigo pointer-events-none absolute right-0 top-40 h-[700px] w-[700px]" />
      <motion.div style={{ y: yGlow }} className="ambient-magenta pointer-events-none absolute left-1/3 bottom-0 h-[500px] w-[500px]" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 lg:grid-cols-12 lg:px-8">
        <motion.div style={{ opacity }} className="lg:col-span-7">
          <motion.a
            href="https://advertising.amazon.com/partners/directory/details/amzn1.ads1.ma1.43nrsaw1n7hzojqbw13e9mycx/DNA-Digital?sref_=user"
            target="_blank"
            rel="noreferrer"
            data-testid="hero-partner-badge"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6 inline-block transition-transform hover:scale-[1.03]"
          >
            <img src="/amazon-verified-partner.png?v=2" alt="Amazon Ads Verified Partner — DNA eCommerce Ads" className="h-24 w-auto rounded-xl" />
          </motion.a>

          <h1 className="font-display text-[13vw] font-black leading-[0.92] tracking-tighter sm:text-7xl lg:text-[5.2rem]">
            <MaskedLines
              lines={["We scale eCommerce", <>brands on <span className="text-gradient">Amazon.</span></>]}
              lineClassName=""
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-7 max-w-xl text-lg font-light leading-relaxed text-muted"
          >
            Performance-first Amazon Ads management, A+ content and full-funnel marketing —
            engineered to grow revenue and lower ACOS. 1 Lakh+ products sold. ROAS up to 11.4x.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-6"
          >
            <div className="flex items-center gap-2 text-sm text-muted">
              <TrendingUp className="h-4 w-4 text-orange" /> Full-funnel DSP experts
            </div>
            <div className="flex items-center gap-2 text-sm text-muted">
              <ShieldCheck className="h-4 w-4 text-magenta" /> Trusted by global brands
            </div>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yCard }} className="lg:col-span-5" data-testid="hero-audit-card">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="glass tracing-card rounded-2xl p-6 lg:p-7"
          >
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-orange">Free Growth Audit</p>
            <h3 className="mt-2 font-display text-2xl font-bold">Book your free audit</h3>
            <p className="mt-1 mb-5 text-sm text-muted">See where your Amazon account is leaking revenue.</p>
            <LeadForm compact idPrefix="hero-lead" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
